/**
 * Доменные перечисления. Значения дублируют enum'ы в prisma/schema.prisma —
 * менять их нужно синхронно, вместе с миграцией.
 */
export const ROLES = ['ADMIN', 'TEACHER', 'STUDENT'];
/**
 * OPEN      — свободное окно, на него можно подать заявку
 * BOOKED    — за окном закреплён ученик
 * COMPLETED — урок проведён, часы списаны с баланса
 * NO_SHOW   — ученик не пришёл (часы списываются, если включено в настройках учителя)
 * CANCELLED — окно отменено, часы не списываются
 */
export const SLOT_STATUSES = ['OPEN', 'BOOKED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'];
/** Статусы, при которых окно больше не занимает место в расписании. */
export const RELEASED_SLOT_STATUSES = ['CANCELLED'];
/** Статусы, при которых урок считается состоявшимся для отчётности. */
export const CONSUMED_SLOT_STATUSES = ['COMPLETED', 'NO_SHOW'];
export const BOOKING_REQUEST_STATUSES = [
    'PENDING',
    'APPROVED',
    'REJECTED',
    'CANCELLED',
    'EXPIRED',
];
export const PAYMENT_METHODS = ['CASH', 'TRANSFER', 'CARD', 'ONLINE'];
export const PAYMENT_STATUSES = [
    'PENDING',
    'SUCCEEDED',
    'FAILED',
    'REFUNDED',
    'CANCELLED',
];
/**
 * Причина движения по реестру часов. Баланс ученика — это всегда сумма
 * `deltaMinutes` всех записей, отдельного изменяемого поля с балансом нет.
 */
export const HOUR_ENTRY_REASONS = [
    'PAYMENT', // оплата пакета часов, delta > 0
    'LESSON', // проведённый урок, delta < 0
    'ADJUSTMENT', // ручная правка админом, delta любой
    'REFUND', // возврат средств, delta < 0
    'COMPENSATION', // компенсация за отменённый учителем урок, delta > 0
];
/** Длительность урока по умолчанию, минуты. */
export const DEFAULT_LESSON_MINUTES = 60;
/**
 * Из чего ученик выбирает длительность занятия. Инструктор длительность
 * не назначает — он задаёт только часы работы, а сколько ездить, решает
 * ученик при записи.
 */
export const LESSON_MINUTES_OPTIONS = [60, 90, 120, 150, 180];
/** Самое короткое занятие — им же меряется, влезает ли ещё одно в остаток дня. */
export const MIN_LESSON_MINUTES = LESSON_MINUTES_OPTIONS[0];
/**
 * Шаг между вариантами начала занятия. Отсчитывается от начала свободного
 * куска: после занятия, кончившегося в 13:30, следующее начинается в 13:30.
 *
 * Полчаса, а не час: занятия ставят и на 14:30, а часовой шаг такое время
 * просто не предлагал. Полчаса — это ещё и минимальная высота строки в
 * календаре записи, которая проходит по размеру цели нажатия.
 */
export const BOOKING_START_STEP_MINUTES = 30;
/**
 * Дни недели в порядке, привычном для расписания: с понедельника.
 * Значения совпадают с Date.getDay(), поэтому воскресенье — ноль в конце списка.
 */
export const WEEKDAYS = [
    { value: 1, short: 'Пн', label: 'Понедельник' },
    { value: 2, short: 'Вт', label: 'Вторник' },
    { value: 3, short: 'Ср', label: 'Среда' },
    { value: 4, short: 'Чт', label: 'Четверг' },
    { value: 5, short: 'Пт', label: 'Пятница' },
    { value: 6, short: 'Сб', label: 'Суббота' },
    { value: 0, short: 'Вс', label: 'Воскресенье' },
];
/**
 * График по умолчанию: занятия идут в субботу, воскресенье и понедельник
 * с 09:00 до 18:00, остальные дни — выходные.
 *
 * Часы проставлены всем семи дням, включая выходные: инструктор или админ
 * включает день галочкой в редакторе графика и сразу получает 09:00–18:00,
 * а не пустой интервал, который пришлось бы выставлять заново.
 */
export const DEFAULT_WORKING_HOURS = [
    { weekday: 1, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: true },
    { weekday: 2, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: false },
    { weekday: 3, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: false },
    { weekday: 4, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: false },
    { weekday: 5, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: false },
    { weekday: 6, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: true },
    { weekday: 0, startMinute: 9 * 60, endMinute: 18 * 60, isEnabled: true },
];
/** Сколько живёт публичная ссылка на расписание, если срок не задан явно, дни. */
export const DEFAULT_SHARE_LINK_TTL_DAYS = 30;
/** За сколько часов до начала ученик ещё может отменить бронь сам. */
export const SELF_CANCEL_DEADLINE_HOURS = 12;
export const SUPPORTED_CURRENCIES = ['RUB', 'KZT', 'USD', 'EUR'];
export const ROLE_LABELS = {
    ADMIN: 'Администратор',
    TEACHER: 'Инструктор',
    STUDENT: 'Ученик',
};
export const SLOT_STATUS_LABELS = {
    OPEN: 'Свободно',
    BOOKED: 'Забронировано',
    COMPLETED: 'Проведён',
    NO_SHOW: 'Не пришёл',
    CANCELLED: 'Отменён',
};
export const BOOKING_REQUEST_STATUS_LABELS = {
    PENDING: 'Ожидает ответа',
    APPROVED: 'Подтверждена',
    REJECTED: 'Отклонена',
    CANCELLED: 'Отменена',
    EXPIRED: 'Истекла',
};
export const PAYMENT_METHOD_LABELS = {
    CASH: 'Наличные',
    TRANSFER: 'Перевод',
    CARD: 'Карта',
    ONLINE: 'Онлайн-оплата',
};
export const PAYMENT_STATUS_LABELS = {
    PENDING: 'Ожидает оплаты',
    SUCCEEDED: 'Оплачено',
    FAILED: 'Ошибка оплаты',
    REFUNDED: 'Возвращено',
    CANCELLED: 'Отменено',
};
export const HOUR_ENTRY_REASON_LABELS = {
    PAYMENT: 'Оплата',
    LESSON: 'Занятие',
    ADJUSTMENT: 'Корректировка',
    REFUND: 'Возврат',
    COMPENSATION: 'Компенсация',
};
//# sourceMappingURL=constants.js.map